import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Stack,
  Autocomplete
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import CustomAxios from '../utils/CustomAxios';
import AdminSidebar from './adminSideBar';
import Header1 from './header1';
import { countries as countries_json } from "../countries";

const CurrencyConversion = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [formData, setFormData] = useState({
    country: '',
    currency_code: '',
    currency_symbol: '',
    intake_percentage: '',
    outtake_percentage: '',
    convenience_fee_percentage: '',
    is_active: true
  });

  // Fetch countries data
  const fetchCountries = async () => {
    try {
      const response = { data: countries_json };
      return response.data.map((country) => ({
        value: country.cca2,
        label: country.name.common,
        currencies: country.currencies || {},
        flag: country.flags?.png,
      }));
    } catch (error) {
      console.error("Error processing countries:", error);
      return [];
    }
  };

  // Fetch currency conversions
  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const response = await CustomAxios.get('currency-conversions/');
      setCurrencies(response.data);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const countryOptions = await fetchCountries();
      setCountries(countryOptions);
      await fetchCurrencies();
    };
    initializeData();
  }, []);

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        intake_percentage: parseFloat(formData.intake_percentage),
        outtake_percentage: parseFloat(formData.outtake_percentage),
        convenience_fee_percentage: parseFloat(formData.convenience_fee_percentage) || 0
      };

      if (editingCurrency) {
        await CustomAxios.put(`currency-conversions/${editingCurrency.id}/`, data);
      } else {
        await CustomAxios.post('currency-conversions/', data);
      }

      fetchCurrencies();
      handleClose();
    } catch (error) {
      console.error('Error saving currency:', error);
    }
  };

  // Handle edit
  const handleEdit = (currency) => {
    setEditingCurrency(currency);
    // Find the corresponding country object for the selected country
    const countryObj = countries.find(c => c.label === currency.country);
    setSelectedCountry(countryObj || null);
    setFormData({
      country: currency.country,
      currency_code: currency.currency_code,
      currency_symbol: currency.currency_symbol || '',
      intake_percentage: currency.intake_percentage,
      outtake_percentage: currency.outtake_percentage,
      convenience_fee_percentage: currency.convenience_fee_percentage || '',
      is_active: currency.is_active
    });
    setOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this currency conversion?')) {
      try {
        await CustomAxios.delete(`currency-conversions/${id}/`);
        fetchCurrencies();
      } catch (error) {
        console.error('Error deleting currency:', error);
      }
    }
  };

  // Handle country selection
  const handleCountryChange = (event, value) => {
    setSelectedCountry(value);
    if (value) {
      // Auto-populate currency information
      const currencyCodes = Object.keys(value.currencies);
      const currencyCode = currencyCodes[0]; // Get first currency code
      const currencyInfo = value.currencies[currencyCode];

      setFormData(prev => ({
        ...prev,
        country: value.label,
        currency_code: currencyCode || '',
        currency_symbol: currencyInfo?.symbol || ''
        // Don't override convenience_fee_percentage when country changes
      }));
    } else {
      // Clear fields if no country selected
      setFormData(prev => ({
        ...prev,
        country: '',
        currency_code: '',
        currency_symbol: ''
      }));
    }
  };

  // Handle close dialog
  const handleClose = () => {
    setOpen(false);
    setEditingCurrency(null);
    setSelectedCountry(null);
    setFormData({
      country: '',
      currency_code: '',
      currency_symbol: '',
      intake_percentage: '',
      outtake_percentage: '',
      convenience_fee_percentage: '',
      is_active: true
    });
  };

  return (
    <Stack direction="row" sx={{ bgcolor: { sm: "grey.100", xs: "#fff" }, minHeight: "calc(100vh)" }}>
      <Header1/>
      <Stack direction="row" sx={{ display: "flex", paddingTop: "144px", gap: "24px", mx: "24px", position: "relative", width: "100%" }}>
        <AdminSidebar/>
        <Box sx={{ flex: 1, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Currency Conversion Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Currency
            </Button>
          </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Country</TableCell>
              <TableCell>Currency Code</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>Intake %</TableCell>
              <TableCell>Outtake %</TableCell>
              <TableCell>Convenience Fee %</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currencies.map((currency) => (
              <TableRow key={currency.id}>
                <TableCell>{currency.country}</TableCell>
                <TableCell>
                  <Chip label={currency.currency_code} variant="outlined" />
                </TableCell>
                <TableCell>{currency.currency_symbol || '-'}</TableCell>
                <TableCell>{currency.intake_percentage}%</TableCell>
                <TableCell>{currency.outtake_percentage}%</TableCell>
                <TableCell>{currency.convenience_fee_percentage}%</TableCell>
                <TableCell>
                  <Chip
                    label={currency.is_active ? 'Active' : 'Inactive'}
                    color={currency.is_active ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(currency)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(currency.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCurrency ? 'Edit Currency Conversion' : 'Add Currency Conversion'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.label || ''}
              value={selectedCountry}
              onChange={handleCountryChange}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderOption={(props, option) => (
                <Box
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <img
                    src={option.flag}
                    alt={option.label}
                    style={{ width: 20, height: 15, objectFit: 'cover' }}
                  />
                  {option.label}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  required
                  helperText="Select a country to auto-populate currency info"
                />
              )}
            />
            <TextField
              label="Currency Code (e.g., INR, MYR)"
              value={formData.currency_code}
              onChange={(e) => setFormData({ ...formData, currency_code: e.target.value.toUpperCase() })}
              fullWidth
              required
              helperText="Auto-populated when country is selected"
            />
            <TextField
              label="Currency Symbol (optional)"
              value={formData.currency_symbol}
              onChange={(e) => setFormData({ ...formData, currency_symbol: e.target.value })}
              fullWidth
              helperText="Auto-populated when country is selected"
            />
            <TextField
              label="Intake Percentage"
              type="number"
              value={formData.intake_percentage}
              onChange={(e) => setFormData({ ...formData, intake_percentage: e.target.value })}
              fullWidth
              required
              helperText="Percentage for money intake/conversion in"
            />
            <TextField
              label="Outtake Percentage"
              type="number"
              value={formData.outtake_percentage}
              onChange={(e) => setFormData({ ...formData, outtake_percentage: e.target.value })}
              fullWidth
              required
              helperText="Percentage for money outtake/conversion out"
            />
            <TextField
              label="Convenience Fee Percentage"
              type="number"
              value={formData.convenience_fee_percentage}
              onChange={(e) => setFormData({ ...formData, convenience_fee_percentage: e.target.value })}
              fullWidth
              helperText="Convenience fee percentage of base amount (e.g., 2.50 for 2.5%)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCurrency ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </Stack>
    </Stack>
  );
};

export default CurrencyConversion;
